import { useNavigate } from 'react-router-dom';
import { Subscription } from '../../types';
import DataTable, { Column } from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import { format, parseISO } from 'date-fns';

interface SubscriptionsTableProps {
  subscriptions: Subscription[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export default function SubscriptionsTable({
  subscriptions,
  loading,
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
}: SubscriptionsTableProps) {
  const navigate = useNavigate();

  const columns: Column<Subscription>[] = [
    {
      key: 'clientName',
      header: 'Client',
      sortable: true,
      render: (sub) => (
        <p className="font-medium text-foreground">
          {sub.clientName || 'Unknown'}
        </p>
      ),
    },
    {
      key: 'productName',
      header: 'Product',
      sortable: true,
      render: (sub) => (
        <div>
          <p className="font-medium text-foreground">
            {sub.productName}
          </p>
          <p className="text-xs text-muted-foreground">
            {sub.sku}
          </p>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty / Used',
      sortable: true,
      render: (sub) => (
        <div className="text-sm">
          <span className="font-medium">{sub.quantity}</span>
          {sub.assignedQuantity !== null && (
            <span className="text-muted-foreground">
              {' '}/ {sub.assignedQuantity}
            </span>
          )}
          {sub.assignedQuantity !== null && sub.quantity > 0 && (
            <div className="w-full bg-muted rounded-full h-1.5 mt-1">
              <div
                className={`h-1.5 rounded-full ${
                  (sub.assignedQuantity / sub.quantity) * 100 < 50
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{
                  width: `${Math.min((sub.assignedQuantity / sub.quantity) * 100, 100)}%`,
                }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'renewalDate',
      header: 'Renewal',
      sortable: true,
      render: (sub) => (
        <span className="text-sm">
          {sub.renewalDate ? format(parseISO(sub.renewalDate), 'MMM d, yyyy') : 'N/A'}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      sortable: true,
      render: (sub) => <StatusBadge status={sub.source} type="source" />,
    },
    {
      key: 'autoRenew',
      header: 'Auto-Renew',
      sortable: true,
      render: (sub) => (
        <StatusBadge
          status={sub.autoRenew ? 'auto-renew-on' : 'auto-renew-off'}
          type="autoRenew"
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (sub) => (
        <div className="space-y-1">
          <StatusBadge status={sub.status} type="subscription" />
          {sub.futureCancellationSubmitted && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
              Cancel Submitted
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={subscriptions}
      keyExtractor={(sub) => sub.id}
      loading={loading}
      emptyMessage="No subscriptions found"
      onRowClick={(sub) => navigate(`/subscriptions/${sub.id}`)}
      pagination={{
        page,
        totalPages,
        total,
        pageSize,
        onPageChange,
      }}
    />
  );
}
